import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_KEYS_PAGE_SIZE } from 'api/meedioBackend/apiKeys/constants';
import { listApiKeys } from 'api/meedioBackend/apiKeys/listApiKeys';
import { ApiKeyItem, ApiKeyStatus } from 'api/meedioBackend/apiKeys/types';
import logger from 'utils/logging/faro';

import { ApiKeysListResult } from './useApiKeys';

interface StreamCursor {
  page: number;
  loaded: number;
  total: number;
  hasMore: boolean;
}

const createCursor = (): StreamCursor => ({ page: 1, loaded: 0, total: 0, hasMore: true });

const byCreatedAtDesc = (a: ApiKeyItem, b: ApiKeyItem): number =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

/**
 * Loads the Expired sub-tab by running two independent paginated streams
 * (`status=expired` and `status=revoked`) and merging them by `created_at DESC`.
 * Refs guard concurrent fetches; pass the backend workspace UUID.
 */
const useExpiredApiKeys = (workspaceUuid: string | null): ApiKeysListResult => {
  const [expiredItems, setExpiredItems] = useState<ApiKeyItem[]>([]);
  const [revokedItems, setRevokedItems] = useState<ApiKeyItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(false);

  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const expiredRef = useRef<StreamCursor>(createCursor());
  const revokedRef = useRef<StreamCursor>(createCursor());
  // Bumped on every workspace switch; in-flight responses from a previous
  // workspace are discarded so stale keys never render on the new workspace.
  const epochRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchStream = useCallback(
    (status: Extract<ApiKeyStatus, 'expired' | 'revoked'>, cursor: StreamCursor, replace: boolean) => {
      if (!replace && !cursor.hasMore) return Promise.resolve(null);
      return listApiKeys({
        workspaceId: workspaceUuid as string,
        status,
        page: replace ? 1 : cursor.page,
        limit: API_KEYS_PAGE_SIZE,
      });
    },
    [workspaceUuid]
  );

  const applyStream = useCallback(
    (
      cursor: StreamCursor,
      response: Awaited<ReturnType<typeof listApiKeys>> | null,
      replace: boolean,
      setStreamItems: (updater: (prev: ApiKeyItem[]) => ApiKeyItem[]) => void
    ) => {
      if (!response) return;

      cursor.total = response.pagination.total;
      cursor.loaded = (replace ? 0 : cursor.loaded) + response.items.length;
      cursor.page = (replace ? 1 : cursor.page) + 1;
      cursor.hasMore = cursor.loaded < cursor.total;
      setStreamItems((prev) => (replace ? response.items : [...prev, ...response.items]));
    },
    []
  );

  const fetchStreams = useCallback(
    async (replace: boolean) => {
      if (!workspaceUuid || isFetchingRef.current) return;

      const epoch = epochRef.current;
      isFetchingRef.current = true;
      if (mountedRef.current) setIsLoading(true);

      const expired = expiredRef.current;
      const revoked = revokedRef.current;

      try {
        const [expiredResponse, revokedResponse] = await Promise.all([
          fetchStream('expired', expired, replace),
          fetchStream('revoked', revoked, replace),
        ]);
        if (!mountedRef.current || epoch !== epochRef.current) return;

        applyStream(expired, expiredResponse, replace, setExpiredItems);
        applyStream(revoked, revokedResponse, replace, setRevokedItems);

        setHasMore(expired.hasMore || revoked.hasMore);
        setError(null);
      } catch (fetchError: unknown) {
        if (!mountedRef.current || epoch !== epochRef.current) return;
        logger.error('Failed to load expired API keys', fetchError);
        setError(fetchError);
      } finally {
        // Only the current epoch's fetch owns the loading/fetching flags.
        if (epoch === epochRef.current) {
          if (mountedRef.current) setIsLoading(false);
          isFetchingRef.current = false;
        }
      }
    },
    [workspaceUuid, fetchStream, applyStream]
  );

  const loadMore = useCallback(() => {
    if (isFetchingRef.current) return;
    if (!expiredRef.current.hasMore && !revokedRef.current.hasMore) return;
    void fetchStreams(false);
  }, [fetchStreams]);

  const refresh = useCallback(() => {
    expiredRef.current = createCursor();
    revokedRef.current = createCursor();
    void fetchStreams(true);
  }, [fetchStreams]);

  useEffect(() => {
    epochRef.current += 1;
    setExpiredItems([]);
    setRevokedItems([]);
    setError(null);
    setHasMore(false);
    expiredRef.current = createCursor();
    revokedRef.current = createCursor();
    isFetchingRef.current = false;

    if (!workspaceUuid) return;
    void fetchStreams(true);
  }, [workspaceUuid, fetchStreams]);

  const items = useMemo(() => [...expiredItems, ...revokedItems].sort(byCreatedAtDesc), [expiredItems, revokedItems]);

  return { items, isLoading, error, hasMore, loadMore, refresh };
};

export default useExpiredApiKeys;
